import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import useUser from '../global/useUser';
import { updateBookingSite } from '../../services/bookingSite';
import { commitGallery } from '../../services/gallery';
import { commitFaqs } from '../../services/faqs';
import {
  BookingSiteSettings,
  UpdateBookingSitePayload,
} from '../../types/bookingSite';
import { DraftFaq } from '../../types/faqs';
import { galleryQueryKey } from '../gallery/useGetGallery';
import { faqsQueryKey } from '../faqs/useGetFaqs';
import useGetBookingSite from './useGetBookingSite';

interface PublishInput {
  settings: UpdateBookingSitePayload;
  galleryImageRefs?: string[];
  faqs?: DraftFaq[];
}

interface Options {
  onSuccess?: (result: BookingSiteSettings) => void | Promise<void>;
  /** Return true when the failure is shown in the form instead of a toast. */
  onError?: (error: unknown) => boolean;
  /** Real ids for rows the draft only knew as placeholders. */
  onFaqsCommitted?: (faqs: DraftFaq[]) => void;
}

function useUpdateBookingSite({
  onSuccess,
  onError,
  onFaqsCommitted,
}: Options = {}) {
  const { user } = useUser();
  const { refetch } = useGetBookingSite();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ settings, galleryImageRefs, faqs }: PublishInput) => {
      if (!user?.business?.id) return null;
      if (galleryImageRefs !== undefined) {
        await commitGallery(
          { kind: 'booking-site', businessId: user.business.id },
          galleryImageRefs,
        );
      }
      if (faqs !== undefined) {
        onFaqsCommitted?.(await commitFaqs(user.business.id, faqs));
      }
      return updateBookingSite(user.business.id, settings);
    },
    onSuccess: async (result, { galleryImageRefs, faqs }) => {
      if (!result) return;
      await refetch();
      if (galleryImageRefs !== undefined && user?.business?.id) {
        await queryClient.invalidateQueries({
          queryKey: galleryQueryKey({
            kind: 'booking-site',
            businessId: user.business.id,
          }),
        });
      }
      if (faqs !== undefined && user?.business?.id) {
        await queryClient.invalidateQueries({
          queryKey: faqsQueryKey(user.business.id),
        });
      }
      await onSuccess?.(result);
    },
    onError: error => {
      if (onError?.(error)) return;
      toast.error('Could not publish your changes. Please try again.');
    },
  });
}

export default useUpdateBookingSite;
